/**
 * GroupedBadge Component
 *
 * Displays a single badge for 4+ threads on the same element.
 * Click opens a picker menu to select which thread to view.
 *
 * Visual states:
 * - Default: white bg, grey text, grey border
 * - Hover: blue border, blue text
 * - Active (picker open): blue bg, white text, blue ring
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ThreadPicker } from './ThreadPicker';
import type { Comment, TeamMember } from '../../lib/types';
import { tokens } from '../../styles/tokens';
import { useCommentPositioning } from '../../hooks/useCommentPositioning';

export interface GroupedBadgeProps {
  /** Threads grouped on this element (4+) */
  threads: Comment[];
  /** Position for the badge (top-right of element) */
  position: { top: number; left: number };
  /** Callback when a thread is selected */
  onThreadSelect: (comment: Comment, pinElement: HTMLElement) => void;
  /** Map of author ID to team member info */
  authors: Map<string, TeamMember>;
  /** Map of comment ID to reply count */
  replyCounts: Map<string, number>;
  /** Set of unread comment IDs */
  unreadCommentIds?: Set<string>;
  /** Whether sidebar is open (for collision detection) */
  isSidebarOpen?: boolean;
  /** Element ID for finding this badge (used for deep linking) */
  elementId: string;
}

export const GroupedBadge: React.FC<GroupedBadgeProps> = ({
  threads,
  position,
  onThreadSelect,
  authors,
  replyCounts,
  unreadCommentIds,
  isSidebarOpen = false,
  elementId,
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const badgeRef = useRef<HTMLButtonElement>(null);

  // Setup floating-ui positioning for the thread picker
  const {
    refs: pickerRefs,
    floatingStyles: pickerFloatingStyles,
    isPositioned: isPickerPositioned,
  } = useCommentPositioning({
    isSidebarOpen,
    offsetDistance: 8,
    viewportPadding: 16,
    maxWidth: 288,
    maxHeight: 300,
  });

  // Callback ref that sets both our local ref and floating-ui's ref
  const setBadgeRef = useCallback((node: HTMLButtonElement | null) => {
    badgeRef.current = node;
    pickerRefs.setReference(node);
  }, [pickerRefs]);

  // Close picker on ESC key
  useEffect(() => {
    if (!isPickerOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPickerOpen(false);
        badgeRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPickerOpen]);

  // Close picker on click outside
  useEffect(() => {
    if (!isPickerOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (badgeRef.current && !badgeRef.current.contains(target)) {
        // Check if click is inside the picker
        const picker = document.querySelector('[data-testid="thread-picker"]');
        if (picker && !picker.contains(target)) {
          setIsPickerOpen(false);
        }
      }
    };

    // Use setTimeout to avoid immediately closing from the click that opened it
    const timeoutId = setTimeout(() => {
      window.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('click', handleClickOutside);
    };
  }, [isPickerOpen]);

  const handleBadgeClick = () => {
    setIsPickerOpen(!isPickerOpen);
  };

  const handleThreadSelect = (comment: Comment) => {
    setIsPickerOpen(false);
    if (badgeRef.current) {
      onThreadSelect(comment, badgeRef.current);
    }
  };

  const handlePickerClose = () => {
    setIsPickerOpen(false);
    badgeRef.current?.focus();
  };

  const threadCount = threads.length;

  return (
    <>
      <button
        ref={setBadgeRef}
        type="button"
        data-testid="grouped-badge"
        data-multidev-sdk="true"
        data-element-id={elementId}
        onClick={handleBadgeClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label={`${threadCount} threads, click to view`}
        aria-expanded={isPickerOpen}
        aria-haspopup="listbox"
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: `6px ${tokens.spacing.s}`,
          backgroundColor: isPickerOpen ? tokens.colors.primary : tokens.colors.bgDefault,
          color: isPickerOpen
            ? tokens.colors.textReverse
            : isHovered
              ? tokens.colors.primary
              : tokens.colors.textSecondary,
          border: `1px solid ${isHovered || isPickerOpen ? tokens.colors.primary : tokens.colors.borderDefault}`,
          borderRadius: '16px',
          fontSize: '13px',
          fontWeight: tokens.typography.fontWeight.medium,
          cursor: 'pointer',
          boxShadow: isPickerOpen
            ? `0 0 0 3px ${tokens.colors.primaryLight}, ${tokens.elevation.raised}`
            : tokens.elevation.raised,
          zIndex: 999997,
          pointerEvents: 'auto',
          transition: `top 50ms linear, left 50ms linear, background-color ${tokens.animation.duration} ${tokens.animation.timing}, color ${tokens.animation.duration} ${tokens.animation.timing}, border-color ${tokens.animation.duration} ${tokens.animation.timing}, box-shadow ${tokens.animation.duration} ${tokens.animation.timing}`,
          outline: 'none',
        }}
      >
        {/* Message icon */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {threadCount} threads
      </button>

      {/* Thread picker popover */}
      {isPickerOpen && (
        <ThreadPicker
          threads={threads}
          authors={authors}
          replyCounts={replyCounts}
          onSelect={handleThreadSelect}
          onClose={handlePickerClose}
          floatingRef={pickerRefs.setFloating}
          floatingStyles={pickerFloatingStyles}
          isPositioned={isPickerPositioned}
          unreadCommentIds={unreadCommentIds}
        />
      )}
    </>
  );
};
